import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { buildingSettingsStore } from "../BuildingSettingsStore";
import { observer } from "mobx-react-lite";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import { IFC_PARSER_CHOICES } from "utils";
import { IFCParserChoices } from "../../../../api-client";
import DropdownCheckbox from "components/DropdownCheckbox";
import Checkbox from "components/Checkbox";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

const availableIFCEntities = [
  "IfcBeam",
  "IfcBuildingElement",
  "IfcBuildingElementProxy",
  "IfcChimney",
  "IfcColumn",
  "IfcCovering",
  "IfcCurtainWall",
  "IfcDoor",
  "IfcFooting",
  "IfcMember",
  "IfcPile",
  "IfcPlate",
  "IfcRailing",
  "IfcRamp",
  "IfcRampFlight",
  "IfcRoof",
  "IfcShadingDevice",
  "IfcSlab",
  "IfcStair",
  "IfcStairFlight",
  "IfcWall",
  "IfcWindow",
];
const defaultSplittedIfcEntities = [
  "IfcWall",
  "IfcSlab",
  "IfcCovering",
  "IfcRoof",
  "IfcPlate",
];

const IfcParserConfiguration = () => {
  const { t } = useTranslation();
  const { settings } = buildingSettingsStore;
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const onChangeSelection = (selection: BGItem | undefined) => {
    buildingSettingsStore.onChangeValue(selection?.id, "ifc_parser");

    const splittedIfcEntities =
      buildingSettingsStore.settings.ingestion_options
        ?.ifc_entities_with_splitting;

    // empty list is a valid value (no entities split). Only set if null or undefined.
    const shouldSetDefault =
      selection?.id === IFCParserChoices.V20250 &&
      (splittedIfcEntities === null || splittedIfcEntities === undefined);
    if (shouldSetDefault) {
      buildingSettingsStore.onChangeIngestionOption(
        "ifc_entities_with_splitting",
        defaultSplittedIfcEntities
      );
    }
  };

  const ingestionOptions = settings.ingestion_options ?? {};

  const entityOptions = availableIFCEntities.map((name) => ({
    id: name,
    name,
  }));

  const splittingList = ingestionOptions.ifc_entities_with_splitting;
  const splitEntities =
    splittingList == null
      ? defaultSplittedIfcEntities
      : availableIFCEntities.filter((e) => splittingList.includes(e));

  const checkedEntities = splitEntities.map((name) => ({ id: name, name }));

  const onSelectEntities = (selected: { name: string }[]) => {
    const selectedNames = selected.map((o) => o.name);
    buildingSettingsStore.onChangeIngestionOption(
      "ifc_entities_with_splitting",
      selectedNames
    );
  };

  const advancedOptions = [
    {
      id: "prepend_name_from_parent_in_parts" as const,
      title: t("buildingSettings.prependNameFromParentInParts"),
      subTitle: t("buildingSettings.prependNameFromParentInPartsDesc"),
    },
    {
      id: "parent_is_assigned_children_materials" as const,
      title: t("buildingSettings.parentIsAssignedChildrenMaterials"),
      subTitle: t("buildingSettings.parentIsAssignedChildrenMaterialsDesc"),
    },
    {
      id: "merge_single_element_part_with_parent" as const,
      title: t("buildingSettings.mergeSingleElementPartWithParent"),
      subTitle: t("buildingSettings.mergeSingleElementPartWithParentDesc"),
    },
  ];

  const notesOther = (
    <div className="text-gray-700 text-sm font-normal">
      <p>Wesentliche Anpassungen zur Version 1</p>
      <ul className="ml-4 list-disc ">
        <li>Grundlegende Unterstützung für IFC 2x3, IFC 4.</li>
        <li>
          Property Sets und Base Quantities werden grundlegend ausgelesen und
          als Objekteigenschaften importiert.
        </li>
        <li>
          Mehrschichtige IFC-Objekte werden aufgetrennt und als einzelne Objekte
          importiert.
        </li>
        <li>
          Unterstützung des Concular Property Sets, für eine detaillierte
          Übermittlung von Objekteigenschaften.
        </li>
      </ul>
    </div>
  );
  const notesV2025 = (
    <div className="text-gray-700 text-sm font-normal">
      <p>Wesentliche Anpassungen zur Version 2</p>
      <ul className="ml-4 list-disc ">
        <li>
          Grundlegende Unterstützung für IFC 2x3 und IFC 4 (Inkl. 4.0 ADD2, 4.3
          RC1 und 4.3 ADD2)
        </li>
        <li>
          Property Sets und Base Quantities werden ausgelesen und als
          Objekteigenschaften importiert
        </li>
        <li>
          Mehrschichtige IFC-Objekte werden aufgetrennt und als einzelne Objekte
          importiert
        </li>
        <li>
          Unterstützung des Concular Property Sets, für eine detaillierte
          Übermittlung aller Objekteigenschaften
        </li>
        <li>Automatische Einheitenerkennung und -umrechnung</li>
        <li>Unterstützung benutzerdefinierter Eigenschaften für IfcTypes</li>
        <li>
          Ursprüngliche Properties, dessen Werte für die Objekteigenschaften
          übernommen wurden, können zurückverfolgt werden
        </li>
        <li>
          Erweiterte Typenunterstützung für Fließkomma- und Boolesche-Werte,
          ermöglichen eine bessere Usability
        </li>
      </ul>
    </div>
  );

  return (
    <div>
      <div className="text-lg font-semibold mb-2">
        {t("buildingSettings.ifcParserConfigTitle")}
      </div>
      <div className="text-sm text-gray-700 pb-5">
        {t("buildingSettings.ifcParserConfigSubtitle")}
      </div>

      <div className="flex flex-col justify-between w-full gap-4">
        <ButtonGroups
          items={IFC_PARSER_CHOICES}
          current={IFC_PARSER_CHOICES.find((e) => e.id == settings.ifc_parser)}
          required
          containerClassName="w-full"
          groupClassName="!mt-0"
          name="ifc_parser"
          optionClsssName="whitespace-nowrap"
          onChange={onChangeSelection}
        />
        {settings.ifc_parser == IFCParserChoices.V20250
          ? notesV2025
          : notesOther}

        {settings.ifc_parser == IFCParserChoices.V20250 && (
          <>
            <div>
              <DropdownCheckbox
                items={entityOptions}
                displayKey="name"
                checkedItems={checkedEntities}
                handleSelect={onSelectEntities}
                containerClassName="mr-0 sm:mr-3 min-w-[146px]"
                optionsClassName="z-40 !w-auto"
                showSelected
                labelName={t("buildingSettings.ifcEntitiesNoSplitting")}
                placeholder={t(
                  "buildingSettings.ifcEntitiesNoSplittingPlaceholder"
                )}
              />
              <div className="text-gray-500 pt-1 text-sm font-normal">
                {t("buildingSettings.ifcEntitiesNoSplittingDesc")}
              </div>
            </div>

            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-indigo-700 transition"
                onClick={() => setAdvancedOpen((o) => !o)}
              >
                {advancedOpen ? (
                  <IconChevronUp className="w-4 h-4" />
                ) : (
                  <IconChevronDown className="w-4 h-4" />
                )}
                {t("buildingSettings.advanced")}
              </button>
              {advancedOpen && (
                <div className="mt-3 flex flex-col gap-4">
                  {advancedOptions.map((option) => (
                    <div className="flex items-start" key={option.id}>
                      <Checkbox
                        id={option.id}
                        checked={Boolean(ingestionOptions[option.id])}
                        onChange={(e) =>
                          buildingSettingsStore.onChangeIngestionOption(
                            option.id,
                            e.currentTarget.checked
                          )
                        }
                        className="mr-2 mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          {option.title}
                        </div>
                        <div className="text-sm font-normal text-gray-500">
                          {option.subTitle}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default observer(IfcParserConfiguration);
